/**
 * [!] SECURITY WARNING [!]
 *
 * This component uses `dangerouslySetInnerHTML` to insert raw SVG content.
 * The SVG is inserted as-is without any sanitization or security checks.
 *
 * IMPORTANT: Only use this component with SVGs from trusted sources.
 * Malicious SVGs could execute scripts or contain harmful content.
 */

import { SVGProps, useEffect, useMemo, useState } from 'react';

const applySvgProps = (
  svgString: string,
  props?: SVGProps<SVGSVGElement> & { title?: string }
): string | null => {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgElement = svgDoc.documentElement;
  // Anything else (an HTML error page, a parse error) must not be inlined.
  if (
    svgElement.nodeName.toLowerCase() !== 'svg' ||
    svgDoc.getElementsByTagName('parsererror').length > 0
  ) {
    return null;
  }
  if (!props) return svgString;

  if (props.style) {
    Object.entries(props.style).forEach(([styleKey, styleValue]) => {
      svgElement.style[styleKey as any] = styleValue as string;
    });
  }

  const propsToApply = { ...props };
  delete propsToApply.style;
  delete propsToApply.children;

  Object.entries(propsToApply).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      const attrName = key === 'className' ? 'class' : key;
      svgElement.setAttribute(attrName, String(value));
    }
  });

  if (props.title) {
    const existingTitle = svgElement.querySelector('title');
    if (existingTitle) {
      existingTitle.remove();
    }

    const titleElement = svgDoc.createElement('title');
    titleElement.textContent = props.title;
    svgElement.insertBefore(titleElement, svgElement.firstChild);
  }

  return svgElement.outerHTML;
};

const RemoteSVG = ({
  url,
  svgProps,
  parentProps,
}: {
  url: string;
  svgProps?: SVGProps<SVGSVGElement> & {
    title?: string;
  };
  parentProps?: JSX.IntrinsicElements['div'];
}) => {
  const [svgText, setSvgText] = useState<string | undefined>();
  const [failed, setFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);

  // Keyed on the url alone: callers pass svgProps inline, a new object on every render.
  useEffect(() => {
    let current = true;
    setSvgText(undefined);
    setFailed(false);
    setFallbackFailed(false);
    if (!url || !url.endsWith('.svg')) {
      setFailed(true);
      return undefined;
    }
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (current) setSvgText(text);
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    return () => {
      current = false;
    };
  }, [url]);

  const svgContent = useMemo(
    () => (svgText === undefined ? null : applySvgProps(svgText, svgProps)),
    [svgText, svgProps]
  );

  if (failed || (svgText !== undefined && svgContent === null)) {
    // The browser may still load it as an image; if not, show nothing rather than a broken icon.
    if (fallbackFailed) return null;
    return (
      <img
        src={url}
        alt={svgProps?.title ?? ''}
        className={svgProps?.className}
        onError={() => setFallbackFailed(true)}
      />
    );
  }

  if (!svgContent) return null;

  return (
    <div
      className="inline-block"
      {...parentProps}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

RemoteSVG.defaultProps = {
  title: '',
  svgProps: undefined,
  parentProps: undefined,
};

export default RemoteSVG;
